let _isLocalEnvironment = /localhost|127\.0\.0\.1/.test(window.location.hostname);

const UHC_TRAVEL_INSURANCE_URL = 'https://www.uhc.com/dental-vision-supplemental-plans/travel-insurance';
const UHC_TRAVEL_INSURANCE_GET_QUOTE_URL = 'https://www.uhc.com/dental-vision-supplemental-plans/travel-insurance/get-quote';

const DEFAULT_EDIT_TRIP_URL = '/get-quote/';
const ADOBE_ORG_ID = '8E391C8B533058250A490D4D@AdobeOrg';
const ADOBE_TRACKING_SERVER = 'uhgsafetrip.sc.omtrdc.net';
const UHC_REDIRECT_EXCLUDED_PARAMS = ['preventRedirect', 'agent'];
const UHC_PAGE_REDIRECTS = {
    '/cookie-policy': 'https://www.uhc.com/privacy/safetrip-privacy/cookie-policy',
    '/privacy-policy': 'https://www.uhc.com/privacy/safetrip-privacy/privacy-policy',
    '/health-data-privacy-policy': 'https://www.uhc.com/privacy/safetrip-privacy',
    '/terms-and-conditions': 'https://www.uhc.com/privacy/terms-of-use',
    '/policy-documents': 'https://www.uhc.com/legal/safetrip-policy-documents',
    '/privacy-policy-non-us': 'https://www.uhc.com/privacy/safetrip-privacy/privacy-policy-non-us',
    '/privacy-notice': 'https://www.uhc.com/privacy/safetrip-privacy/privacy-notice-non-us',
    '/terms-and-conditions-non-us': 'https://www.uhc.com/privacy/terms-of-use/terms-of-use-non-us',
    '/make-a-claim': 'https://www.uhc.com/dental-vision-supplemental-plans/travel-insurance/resources/claims',
};

function normalizeRedirectPath(pathname) {
    const path = (pathname || '/').replace(/\/+$/, '') || '/';
    return path.toLowerCase();
}

/**
 * Adobe Visitor ID Service instance for cross-domain ECID handoff to uhc.com.
 * @see https://experienceleague.adobe.com/en/docs/id-service/using/id-service-api/methods/appendvisitorid
 */
function getAdobeVisitor() {
    if (typeof window.Visitor === 'undefined' || typeof window.Visitor.getInstance !== 'function') {
        return null;
    }

    try {
        return window.Visitor.getInstance(ADOBE_ORG_ID, {
            trackingServer: ADOBE_TRACKING_SERVER,
            trackingServerSecure: ADOBE_TRACKING_SERVER,
        });
    } catch (e) {
        return null;
    }
}

function getCookieValue(name) {
    const encodedName = `${encodeURIComponent(name)}=`;
    const parts = document.cookie ? document.cookie.split(';') : [];
    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i].trim();
        if (part.indexOf(encodedName) === 0) {
            return decodeURIComponent(part.slice(encodedName.length));
        }
        if (part.indexOf(`${name}=`) === 0) {
            return decodeURIComponent(part.slice(name.length + 1));
        }
    }
    return null;
}

function getEcidFromAmcvCookie() {
    const cookieValue = getCookieValue(`AMCV_${ADOBE_ORG_ID}`);
    if (!cookieValue) {
        return null;
    }

    const match = cookieValue.match(/(?:^|\|)MCMID\|([^|]+)/);
    return match && match[1] ? match[1] : null;
}

function urlHasAdobeMcEcid(url) {
    try {
        const adobeMc = new URL(url, window.location.origin).searchParams.get('adobe_mc');
        if (!adobeMc) {
            return false;
        }
        return /(?:^|\|)MCMID=/.test(adobeMc);
    } catch (e) {
        return /[?&]adobe_mc=/.test(url) && /MCMID/.test(url);
    }
}

function appendAdobeMcFromEcid(url, ecid) {
    if (!url || !ecid) {
        return url;
    }

    try {
        const target = new URL(url, window.location.origin);
        const adobeMc = [
            `TS=${Math.floor(Date.now() / 1000)}`,
            `MCMID=${ecid}`,
            'MCAID=',
            `MCORGID=${ADOBE_ORG_ID}`,
        ].join('|');
        target.searchParams.set('adobe_mc', adobeMc);
        return target.toString();
    } catch (e) {
        return url;
    }
}

function appendAdobeVisitorIds(url) {
    if (!url) {
        return url;
    }

    try {
        const visitor = getAdobeVisitor();
        if (visitor && typeof visitor.appendVisitorIDsTo === 'function') {
            const withIds = visitor.appendVisitorIDsTo(url);
            // Clear MCAID per Adobe sample so destination generates Analytics ID while keeping ECID.
            const cleaned = String(withIds).replace(/MCAID%3D.*%7CMCORGID/, 'MCAID%3D%7CMCORGID');
            if (urlHasAdobeMcEcid(cleaned)) {
                return cleaned;
            }
        }
    } catch (e) {
        // Fall through to cookie-based adobe_mc.
    }

    return appendAdobeMcFromEcid(url, getEcidFromAmcvCookie()) || url;
}

/**
 * Forward current-page query params (UTMs, etc.) onto the UHC destination.
 * Skips internal SafeTrip params and does not overwrite existing destination params.
 */
function mergeCurrentQueryParams(url) {
    if (!url) {
        return url;
    }

    try {
        const target = new URL(url, window.location.origin);
        const current = new URLSearchParams(window.location.search);

        current.forEach((value, key) => {
            if (!key || value == null || String(value).trim() === '') {
                return;
            }
            if (UHC_REDIRECT_EXCLUDED_PARAMS.indexOf(key) !== -1) {
                return;
            }
            if (target.searchParams.has(key)) {
                return;
            }
            target.searchParams.set(key, value);
        });

        return target.toString();
    } catch (e) {
        return url;
    }
}

function getAvailableEcid() {
    const fromCookie = getEcidFromAmcvCookie();
    if (fromCookie) {
        return fromCookie;
    }

    const visitor = getAdobeVisitor();
    if (!visitor || typeof visitor.getMarketingCloudVisitorID !== 'function') {
        return null;
    }

    try {
        const syncMid = visitor.getMarketingCloudVisitorID();
        return syncMid || null;
    } catch (e) {
        return null;
    }
}

/**
 * Build UHC URL with marketing params immediately.
 * Appends adobe_mc only when ECID is already available (no consent / Launch wait).
 */
function buildUhcRedirectUrl(url) {
    const withMarketingParams = mergeCurrentQueryParams(url);
    const fromApi = appendAdobeVisitorIds(withMarketingParams);

    if (urlHasAdobeMcEcid(fromApi)) {
        return fromApi;
    }

    const ecid = getAvailableEcid();
    if (ecid) {
        return appendAdobeMcFromEcid(withMarketingParams, ecid);
    }

    return withMarketingParams;
}

function navigateToUhc(url) {
    window.location.href = buildUhcRedirectUrl(url);
}

function redirectToUhc() {
    const params = new URLSearchParams(window.location.search);
    const data = readQuoteStorageData({});
    const hasAgentParam = params.has('agent') || !!data.partner_code;
    const preventRedirect = params.has('preventRedirect');

    if (preventRedirect || hasAgentParam) {
        return;
    }

    const path = normalizeRedirectPath(window.location.pathname);

    if (path === '/') {
        if (typeof window.isMemberUser === 'function' && window.isMemberUser()) {
            return;
        }
        navigateToUhc(UHC_TRAVEL_INSURANCE_URL);
        return;
    }

    if (path === '/get-quote') {
        if (typeof window.isMemberUser === 'function' && window.isMemberUser()) {
            return;
        }
        navigateToUhc(UHC_TRAVEL_INSURANCE_GET_QUOTE_URL);
        return;
    }

    if (UHC_PAGE_REDIRECTS[path]) {
        navigateToUhc(UHC_PAGE_REDIRECTS[path]);
    }
}

function readQuoteStorageData(fallback = {}) {
    try {
        const storedData = localStorage.getItem('data');
        if (storedData === null) {
            return fallback;
        }
        let value = JSON.parse(atob(storedData));
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                try {
                    value = JSON.parse(value);
                } catch (e) {
                    /* keep string */
                }
            }
        }
        return value && typeof value === 'object' && !Array.isArray(value) ? value : fallback;
    } catch (e) {
        return fallback;
    }
}

function normalizeEditTripLinkText(text) {
    return (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function isUhcGetQuoteHref(href) {
    if (!href) {
        return false;
    }
    try {
        const url = new URL(href, window.location.origin);
        const host = url.hostname.toLowerCase();
        const path = url.pathname.replace(/\/+$/, '') || '/';
        const isUhcHost = host === 'uhc.com' || host.endsWith('.uhc.com');
        return isUhcHost && path.endsWith('/get-quote');
    } catch (e) {
        return /uhc\.com\/.*get-quote/i.test(href);
    }
}

function isGetQuoteHref(href) {
    if (!href) {
        return false;
    }
    if (isUhcGetQuoteHref(href)) {
        return true;
    }
    try {
        const path = new URL(href, window.location.origin).pathname.replace(/\/+$/, '') || '/';
        return path === '/get-quote' || path.startsWith('/get-quote/');
    } catch (e) {
        const normalized = href.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
        return normalized === '/get-quote'
            || normalized.endsWith('/get-quote')
            || normalized.startsWith('/get-quote/');
    }
}

function getGetQuoteLinkElements(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    return Array.from(scope.querySelectorAll('a[href]')).filter((anchor) => {
        return isGetQuoteHref(anchor.getAttribute('href') || '');
    });
}

function isEditTripLinkText(text) {
    const normalized = normalizeEditTripLinkText(text);
    return normalized === 'trip'
        || normalized === 'edit trip'
        || normalized === 'edit your trip';
}

function isEditTripLinkElement(anchor) {
    if (!anchor || anchor.tagName !== 'A') {
        return false;
    }
    if (!isGetQuoteHref(anchor.getAttribute('href') || '')) {
        return false;
    }
    return true;
}

function applyEditTripLinkRedirects(root = document) {
    if (!readQuoteStorageData({}).quote_session_id) {
        return;
    }
    getGetQuoteLinkElements(root).forEach((anchor) => {
        if (isEditTripLinkElement(anchor)) {
            anchor.setAttribute('href', buildUhcRedirectUrl(UHC_TRAVEL_INSURANCE_GET_QUOTE_URL));
        }
    });
}

function initEditTripLinkRedirects() {
    applyEditTripLinkRedirects();

    $(document).on('mousedown', 'a[href]', function () {
        const href = this.getAttribute('href') || '';
        if (!isGetQuoteHref(href) || !isEditTripLinkElement(this) || !readQuoteStorageData({}).quote_session_id) {
            return;
        }
        this.setAttribute('href', buildUhcRedirectUrl(UHC_TRAVEL_INSURANCE_GET_QUOTE_URL));
    });

    $(document).on('click', 'a[href]', function (event) {
        const href = this.getAttribute('href') || '';
        if (!isGetQuoteHref(href) || !isEditTripLinkElement(this) || !readQuoteStorageData({}).quote_session_id) {
            return;
        }
        event.preventDefault();
        navigateToUhc(UHC_TRAVEL_INSURANCE_GET_QUOTE_URL);
    });

    const productBreakdown = document.getElementById('product-breakdown');
    if (productBreakdown && typeof MutationObserver !== 'undefined') {
        new MutationObserver(applyEditTripLinkRedirects).observe(productBreakdown, { childList: true, subtree: true });
    }
}

const pathsToCheck = [
  '/contact-us/',
  '/partner-contact/',
  '/',
  '/get-quote/',
  '/checkout/travelers/',
  '/checkout/billing/',
  '/checkout/review/',
  '/success/'
];

let pathcontains=function(){
  var currentPath = window.location.pathname;
  var containsPath = pathsToCheck.some(function(path) {
    return currentPath.indexOf(path) !== -1;
  });

  return containsPath;
}

const CSRF_STORAGE_KEY = btoa('st-token');
let csrfRefreshPromise = null;

function saveCsrfToken(csrfToken) {
    if (!csrfToken) {
        return false;
    }

    localStorage.setItem(CSRF_STORAGE_KEY, btoa(JSON.stringify(csrfToken)));

    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
        meta.setAttribute('content', csrfToken);
    }

    return true;
}

function getStoredCsrfToken() {
    return getLocalStorageDataSelf(CSRF_STORAGE_KEY);
}

function parseXhrResponseData(xhr) {
    if (xhr.readyState !== 4 || !xhr.responseText) {
        return null;
    }

    try {
        const parsed = JSON.parse(xhr.responseText);
        return _isLocalEnvironment ? parsed : parsed.data;
    } catch (error) {
        return null;
    }
}

function isUnauthorizedCsrfResponse(xhr) {
    return xhr.readyState === 4 && parseXhrResponseData(xhr) === 'Unauthorized';
}

function readCsrfTokenFromXhr(xhr) {
    if (!xhr || !xhr.getResponseHeader) {
        return null;
    }

    return xhr.getResponseHeader('st-csrf-token');
}

function fetchCsrfToken() {
    if (csrfRefreshPromise) {
        return csrfRefreshPromise;
    }

    csrfRefreshPromise = new Promise((resolve, reject) => {
        if (typeof window.corsRequest !== 'function') {
            csrfRefreshPromise = null;
            reject(new Error('corsRequest unavailable'));
            return;
        }

        window.corsRequest({
            params: JSON.stringify({ uri: 'token' }),
            skipCsrfRetry: true,
            onreadystatechange(event) {
                const xhr = event.target;

                if (xhr.readyState !== 4) {
                    return;
                }

                csrfRefreshPromise = null;

                const csrfToken = readCsrfTokenFromXhr(xhr);

                if (csrfToken && saveCsrfToken(csrfToken)) {
                    resolve(csrfToken);
                    return;
                }

                reject(new Error('Unable to refresh CSRF token'));
            },
            onerror() {
                csrfRefreshPromise = null;
                reject(new Error('Unable to refresh CSRF token'));
            },
        });
    });

    return csrfRefreshPromise;
}

function refreshCsrfTokenFromXhr(xhr) {
    const csrfToken = readCsrfTokenFromXhr(xhr);

    if (csrfToken && saveCsrfToken(csrfToken)) {
        return Promise.resolve(csrfToken);
    }

    return fetchCsrfToken();
}

function getXhrxData(xhr){
  return parseXhrResponseData(xhr) ?? [];
}

function setLocalStorageDataSelf(name,data){
  localStorage.setItem(name, btoa(JSON.stringify(data)));
}

function getLocalStorageDataSelf(name,data){
  try {
    const storedData = localStorage.getItem(name);
    if (storedData === null) {
        throw new Error(`Item '${name}' not found in local storage`);
    }
    const decodedData = atob(storedData);
    const parsedData = JSON.parse(decodedData);

    return parsedData;
} catch (error) {            
    return null;
}
}

function updateOrganizationId(data){
  let params = {
    'uri': 'get-organization-id',
    'method': 'POST',
    'partner_code': data.partner_code
  };

  corsRequest({
      params: JSON.stringify(params),
      onreadystatechange: corsOnReady,
      onerror: corsOnError,
  });

  function corsOnReady(event) {
      let xhr = event.target;
      if (xhr.readyState == 4 && xhr.status == 200) {
        let xhrx = getXhrxData(xhr);
        if(xhrx.organization_id > 0){
          data.organisation_id = xhrx.organization_id;
          setLocalStorageDataSelf('partner_code_changed', false);
          setLocalStorageDataSelf('data', JSON.stringify(data));
        }
      }
  }
  function corsOnError(event) {
      let xhr = event.target
      console.log('Error getting organization id', xhr);
  };
}

export default {
    updateOrganizationId: updateOrganizationId,
    setLocalStorageData: (name,data = {}) => {
       localStorage.setItem(name, btoa(JSON.stringify(data)));
    },
  
    getLocalStorageData: (name) => {
        try {
            const storedData = localStorage.getItem(name);
            if (storedData === null) {
                throw new Error(`Item '${name}' not found in local storage`);
            }

            const decodedData = atob(storedData);
            const parsedData = JSON.parse(decodedData);

            return parsedData;
        } catch (error) {            
            return null;
        }
    },

    /**
     * Same storage as getLocalStorageData but safe to use without JSON.parse (value may already be an object).
     * If legacy double-encoded JSON string is stored, parses one more level.
     */
    getLocalStorageParsed: (name, fallback = null) => {
        try {
            const storedData = localStorage.getItem(name);
            if (storedData === null) {
                return fallback;
            }
            let value = JSON.parse(atob(storedData));
            if (typeof value === 'string') {
                const t = value.trim();
                if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        /* keep string */
                    }
                }
            }
            return value === undefined ? fallback : value;
        } catch (e) {
            return fallback;
        }
    },
    
    corsOnError: (event) => {
        let xhr = event.target
        console.error(xhr.responseText);
    },

    escapeHTML: (str) =>{
        return str.replace(/[&<>"'\/]/g, function (char) {
            switch (char) {
              case '&':
                return '&amp;';
              case '<':
                return '&lt;';
              case '>':
                return '&gt;';
              case '"':
                return '&quot;';
              case '\\':
                return '&#39;';
              case '/':
                return '&#x2F;';
              default:
                return char;
            }
        });
    },
    getXhrxData: getXhrxData,

    saveCsrfToken,
    getStoredCsrfToken,
    isUnauthorizedCsrfResponse,
    fetchCsrfToken,
    refreshCsrfTokenFromXhr,

    getCSRFToken: () => {
        fetchCsrfToken().catch(() => {});
    },
    isLocalEnvironment:()=>{
      return _isLocalEnvironment;
    },
    getAgentDetails:()=>{
      const data = JSON.parse(getLocalStorageDataSelf('data') || '{}');
      let partnerInfo = {};
      const partnerMappings = {
        'partner_code': 'partnerName',
        'producer_1': 'Subproducer_1', 
        'producer_2': 'Subproducer_2',
        'producer_3': 'Subproducer_3',
        'producer_4': 'Subproducer_4'
      };

      Object.entries(partnerMappings).forEach(([key, field]) => {
          if (data[field]) {
              partnerInfo[field] = data[field];
          }
      }); 
      partnerInfo.partner_code = data.partner_code != undefined? data.partner_code : partnerInfo.partnerName;
      return partnerInfo;
    },
    isFromUhc:()=>{
      const data = readQuoteStorageData({});
      return !!(data && data.quote_session_id);
    },
    getEditTripUrl:()=>{
      return readQuoteStorageData({}).quote_session_id
        ? buildUhcRedirectUrl(UHC_TRAVEL_INSURANCE_GET_QUOTE_URL)
        : DEFAULT_EDIT_TRIP_URL;
    },
    isEditTripLink:(anchor)=>{
      return isEditTripLinkElement(anchor);
    },
    applyEditTripLinkRedirects,
    initEditTripLinkRedirects,
    redirectToUhc,
  }